import Axios from 'axios';
import Head from 'next/head';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { deduplArray } from '../../../../src/common/utils/convertUtil';
import {
  formatAlarmDateKst,
  isAlarmActiveForDisplay,
  normalizeAlarmIsActive,
  openLivePlanAlarmFromSchedule,
  openLivePlanReplay,
  readAlarmsFromStorage,
} from '../../../../src/common/utils/liveScheduleAppActions';
import {
  extractProgramArrayFromChannelJson,
  findLiveProgramIndex,
  findTimeAnchorProgramIndex,
  formatScheduleClockForDisplay,
  getApiDate,
  normalizeProgramRowsForSchedule,
  processScheduleResponse,
  toYmdKey,
} from '../../../../src/common/utils/liveScheduleTimeline';
import {
  ensureTypeAosScheduleDocumentScrollLayout,
  restoreTypeAosScheduleDocumentScrollLayout,
  scrollDateTabIntoView,
  scrollScheduleRowBelowTimetableTabs,
} from '../../../../src/common/utils/liveAppScheduleScroll';
import { useLiveAppPageNoGnb } from '../../../../src/common/utils/liveAppPageNoGnb';
import { getLiveChannelType, toPlanAPIUrl } from '../../../../src/common/utils/liveUtil';
import ScheduleInfoDott from '../../../../src/component/m/live/ScheduleInfoDott';

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const CHANNEL_TAB_EDGE_CLONES = 2;
const CHANNEL_TAB_SLIDE_GAP = 8;
/** tab-type002 채널 탭 대략 폭 — 실측 전 SSR·리사이즈 추정용 */
const CHANNEL_TAB_SLIDE_WIDTH_EST = 76;

/** 채널 API 전 SSR·첫 페인트용 기본 탭 (오늘 S01 편성과 짝) */
const BOOTSTRAP_CHANNELS = [{ channelId: 'S01', channelName: 'SBS' }];

/** SSR 시 useLayoutEffect 경고 방지 */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** 패딩 슬라이드 기준 activeIndex → channels 논리 인덱스 */
function channelTabLogicalIndex(activeIndex, cloneCount, len) {
  if (!len) return 0;
  return ((activeIndex - cloneCount) % len + len) % len;
}

/**
 * centeredSlidesBounds — 뷰포트가 넓을수록 맨 앞 채널(SBS)을 가운데 두려면
 * 앞쪽 클론 슬라이드가 더 많아야 함. 부족하면 activeIndex가 밀려 FunE·Sports 등이 디폴트로 잡힘.
 */
function estimateChannelTabEdgeClones(
  viewportWidth,
  slideWidth = CHANNEL_TAB_SLIDE_WIDTH_EST
) {
  if (!viewportWidth) return CHANNEL_TAB_EDGE_CLONES;
  const visible = Math.max(
    1,
    Math.ceil(viewportWidth / (slideWidth + CHANNEL_TAB_SLIDE_GAP))
  );
  return Math.max(CHANNEL_TAB_EDGE_CLONES, Math.ceil(visible / 2));
}

function buildChannelSwiperPad(channels, edgeClones) {
  const len = channels.length;
  if (!len) {
    return { slides: [], cloneCount: 0 };
  }
  if (len === 1) {
    return {
      slides: [channels[0], channels[0], channels[0]],
      cloneCount: 1,
    };
  }
  const k = Math.min(edgeClones, len);
  return {
    slides: [
      ...channels.slice(-k),
      ...channels,
      ...channels.slice(0, k),
    ],
    cloneCount: k,
  };
}

function hasSchedulePlanJson(channelId) {
  const y = new Date().getFullYear();
  return toPlanAPIUrl(String(channelId), y, 1, 1) != null;
}

/** API에서 나온 상대 순서 유지 — TV 전부 다음 Radio 전부 */
function partitionScheduleChannelsTvThenRadio(rows) {
  const tv = [];
  const radio = [];
  (rows || []).forEach((r) => {
    if (r.mediaType === 'Radio') radio.push(r);
    else tv.push(r);
  });
  return [...tv, ...radio];
}

/** m/live 등과 동일: 라디오 멀티오디오 등 channelname 중복 → deduplArray 한 건만 */
function dedupeScheduleRadioTabs(radioRows) {
  if (!radioRows.length) return [];
  const forDedupe = radioRows.map((r) => ({
    channelId: r.channelId,
    channelName: r.channelName,
    mediaType: 'Radio',
    channelname: r.channelName,
    channelid: r.channelId,
  }));
  return deduplArray(forDedupe, 'channelname', 'channelid').map((r) => ({
    channelId: String(r.channelid),
    channelName: r.channelName,
    mediaType: 'Radio',
  }));
}

/** API 응답 순서(또는 TV/Radio 묶은 순서) 유지 + S01(SBS)만 맨 앞 */
function orderScheduleChannelsWithSbsFirst(apiRows) {
  const raw = (apiRows || []).map((c) => ({
    channelId: String(c.channelId),
    channelName: c.channelName || String(c.channelId),
  }));
  const sbsIx = raw.findIndex((r) => r.channelId === 'S01');
  if (sbsIx <= 0) return raw;
  const sbs = raw[sbsIx];
  return [sbs, ...raw.filter((_, i) => i !== sbsIx)];
}

function getOffsetDate(base, offset) {
  const d = new Date(base);
  d.setDate(d.getDate() + offset);
  return d;
}

function organizeByDate(data) {
  const result = {};
  (data || []).forEach((item) => {
    const dk = toYmdKey(item.date);
    if (!dk) return;
    if (!result[dk]) result[dk] = [];
    if (!result[dk].find((ch) => String(ch.channelId) === String(item.channelId))) {
      result[dk].push(item);
    }
  });
  return result;
}

async function fetchScheduleForDate(y, m, d, channelRow) {
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  const dateYmd = `${y}${mm}${dd}`;
  if (!channelRow?.channelId) return [];

  const id = String(channelRow.channelId);
  const url = toPlanAPIUrl(id, y, m, d);
  const wrap = (programs) =>
    processScheduleResponse({
      schedule: [
        {
          date: dateYmd,
          channels: [
            {
              channel: channelRow.channelName || id,
              channel_code: id,
              programs,
            },
          ],
        },
      ],
    });

  if (!url) {
    return wrap([]);
  }

  try {
    const res = await Axios.get(url, { timeout: 5000 });
    const extracted = extractProgramArrayFromChannelJson(res.data);
    const programs = normalizeProgramRowsForSchedule(extracted);
    return wrap(programs);
  } catch (e) {
    console.error(`스케줄 로드 실패 (${id}):`, e?.message || e);
    return wrap([]);
  }
}

async function fetchChannelList() {
  try {
    const res = await Axios.get(process.env.NEXT_PUBLIC_API_LIVE_CHANNELS, { timeout: 3000 });
    const list = res.data?.list || res.data?.data?.list || [];

    const pickId = (ch) => ch.channelid ?? ch.channelId ?? ch.channel_code ?? ch.id;
    const pickName = (ch) => ch.channelname ?? ch.channelName ?? ch.channel ?? ch.name;

    const nameById = new Map();
    list.forEach((ch) => {
      const id = pickId(ch);
      const name = pickName(ch);
      if (!id || !name) return;
      const sid = String(id);
      if (!nameById.has(sid)) nameById.set(sid, name);
    });

    const seen = new Set();
    const candidates = [];

    list.forEach((ch) => {
      const id = pickId(ch);
      if (!id) return;
      const sid = String(id);
      if (ch.type === 'Radio' && ch.onair_yn !== 'Y') return;
      if (!sid || seen.has(sid) || !hasSchedulePlanJson(sid)) return;
      seen.add(sid);
      candidates.push({
        channelId: sid,
        channelName: nameById.get(sid) || sid,
        mediaType: ch.type === 'Radio' ? 'Radio' : 'TV',
      });
    });

    const tvThenRadio = partitionScheduleChannelsTvThenRadio(candidates);
    const tv = tvThenRadio.filter((r) => r.mediaType !== 'Radio');
    const radio = tvThenRadio.filter((r) => r.mediaType === 'Radio');
    return orderScheduleChannelsWithSbsFirst([...tv, ...dedupeScheduleRadioTabs(radio)]);
  } catch (e) {
    console.error('채널 목록 로드 실패:', e);
    return [];
  }
}

const ScheduleProgramItem = React.memo(
  ({
    program,
    channelId,
    channelName,
    jwtToken,
    isAlarmActive,
    onAlarmToggle,
    showAd,
    nowTs,
    scrollAnchorRef,
    programIndex,
    liveRowIndex,
    viewingToday,
    alarmDate,
    isTypeAos,
  }) => {
    const now = typeof nowTs === 'number' ? nowTs : Date.now();
    const isLiveRow =
      viewingToday && liveRowIndex >= 0 && programIndex === liveRowIndex;
    const status = isLiveRow
      ? 'live'
      : now >= program.endTimestamp
        ? 'past'
        : now < program.startTimestamp
          ? 'future'
          : 'past';

    const handleAlarmClick = useCallback(
      (e) => {
        e.preventDefault();
        openLivePlanAlarmFromSchedule({
          channelId,
          channelName,
          programId: program.programId,
          title: program.title,
          alarmDate,
          circleImage: program.circleImage,
          jwtToken,
          isAlarmActive,
          onToggle: onAlarmToggle,
          pageType: 'schedule',
        });
      },
      [
        channelId,
        channelName,
        program.programId,
        program.title,
        program.circleImage,
        alarmDate,
        jwtToken,
        isAlarmActive,
        onAlarmToggle,
      ]
    );

    const handleReplayClick = useCallback(
      (e) => {
        e.preventDefault();
        openLivePlanReplay(program, channelId, { channelName, pageType: 'schedule' });
      },
      [program, channelId]
    );

    /** CurrentProgram 가로 편성과 동일: TV 채널 + 과거 + programId 있을 때만 다시보기 */
    const isTvScheduleChannel = getLiveChannelType(String(channelId)) === 'tv';
    const showReplayButton =
      status === 'past' && isTvScheduleChannel && program.programId;

    return (
      <>
        <div
          ref={scrollAnchorRef}
          className={`schedule_program_w${status === 'past' ? ' type_past' : ''}${
            status === 'live' ? ' type_live' : ''
          }`}
        >
          <div className="schedule_program_inner">
            <div className="schedule_program_cont">
              <div className="title_w">
                <strong className="title_cont">{program.title}</strong>
                <span className="time">{formatScheduleClockForDisplay(program.startTime)}</span>
              </div>
            </div>
            {showReplayButton ? (
              <button
                type="button"
                className="btnIcon01_replay"
                onClick={handleReplayClick}
              >
                <span className="ico_replay">
                  <i className="ir">다시재생</i>
                </span>
              </button>
            ) : null}
            {status === 'live' && (
              <button type="button" className="btnIcon01_live">
                <span className="ico_live02">
                  <span className="txt01">live</span>
                </span>
              </button>
            )}
            {status === 'future' && (
              <button
                type="button"
                className={`btnIcon01_alarm02_toggle${isAlarmActive ? ' active' : ''}`}
                onClick={handleAlarmClick}
              >
                <span
                  className="ico_alarm02_on"
                  style={
                    isTypeAos
                      ? { display: isAlarmActive ? 'block' : 'none' }
                      : undefined
                  }
                >
                  <i className="ir">알림 받기 중</i>
                </span>
                <span
                  className="ico_alarm02_off"
                  style={
                    isTypeAos
                      ? { display: isAlarmActive ? 'none' : 'block' }
                      : undefined
                  }
                >
                  <i className="ir">알림 받기 전</i>
                </span>
              </button>
            )}
          </div>
        </div>
        {showAd && (
          <div className="schedule_contents_ad">
            <div className="ad_banner_w">
              <iframe
                title="배너광고"
                src="/html/m/schedule_mo_middle_320100.htm"
                width="320"
                height="100"
                scrolling="no"
                allowtransparency="true"
                frameBorder={0}
                style={{ border: 0, margin: 0 }}
              />
            </div>
          </div>
        )}
      </>
    );
  }
);

ScheduleProgramItem.displayName = 'ScheduleProgramItem';

function mapChannelRowsForState(list) {
  return (list || []).map((c) => ({
    channelId: String(c.channelId),
    channelName: c.channelName || String(c.channelId),
  }));
}

const SchedulePage = (props) => {
  const { isAndroid } = props;
  useLiveAppPageNoGnb();

  const today = useMemo(() => {
    // 서버(GSSP)가 내려준 KST 날짜를 기준으로 고정 → 서버/클라 하이드레이션 일치 + SSR 편성 키 매칭
    if (props.initialDateYmd && /^\d{8}$/.test(props.initialDateYmd)) {
      const y = parseInt(props.initialDateYmd.slice(0, 4), 10);
      const mo = parseInt(props.initialDateYmd.slice(4, 6), 10) - 1;
      const dd = parseInt(props.initialDateYmd.slice(6, 8), 10);
      return new Date(y, mo, dd);
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, [props.initialDateYmd]);
  const todayStr = useMemo(() => getApiDate(today), [today]);

  // 날짜 범위: 오늘 기준 -7일 ~ +7일 (15일)
  const allDates = useMemo(
    () => Array.from({ length: 15 }, (_, i) => getOffsetDate(today, i - 7)),
    [today]
  );
  const allDatesRef = useRef(allDates);
  allDatesRef.current = allDates;

  const [channels, setChannels] = useState(() => mapChannelRowsForState(props.channels));

  useEffect(() => {
    if ((props.channels || []).length > 0) {
      setChannels(mapChannelRowsForState(props.channels));
    }
  }, [props.channels]);

  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  /**
   * GSSP에서 API를 빼므로 클라에서 로드. 채널·편성을 분리 발사(Promise.all 대기 X)해서
   * 먼저 도착하는 쪽부터 화면에 반영 → 첫 빈 화면 시간 단축.
   */
  const clientScheduleBootstrappedRef = useRef(false);
  const channelsFromServerLen = (props.channels || []).length;
  const hasServerSchedule = (props.scheduleData || []).length > 0;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (channelsFromServerLen > 0) return;
    if (clientScheduleBootstrappedRef.current) return;
    clientScheduleBootstrappedRef.current = true;

    let cancelled = false;
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const d = today.getDate();
    const ymdStr = `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;

    fetchChannelList()
      .then((rawChannels) => {
        if (cancelled) return;
        setChannels(mapChannelRowsForState(rawChannels));
      })
      .catch((e) => console.error('전체편성표 채널 로드:', e));

    /** ensureChannelScheduleLoaded와 동일 키 — 이펙트가 같은 JSON을 다시 치지 않게 미리 등록 */
    scheduleFetchedKeysRef.current.add(`${ymdStr}__S01`);
    /** 서버(GSSP)에서 이미 오늘 S01 편성을 내려준 경우 재호출 생략 (SBS.json 중복 방지) */
    if (!hasServerSchedule) {
      fetchScheduleForDate(y, m, d, { channelId: 'S01', channelName: 'SBS' })
        .then((schedulePayload) => {
          if (cancelled) return;
          setScheduleByDate((prev) => {
            const merged = organizeByDate(schedulePayload);
            const next = { ...prev };
            Object.keys(merged).forEach((k) => {
              next[k] = merged[k];
            });
            return next;
          });
        })
        .catch((e) => console.error('전체편성표 초기 편성 로드:', e));
    }

    return () => {
      cancelled = true;
    };
  }, [today, channelsFromServerLen, hasServerSchedule]);


  /** API 전에도 채널 탭·Swiper 패딩이 SSR HTML 에 그려지도록 S01 폴백 */
  const displayChannels = useMemo(
    () => (channels.length > 0 ? channels : BOOTSTRAP_CHANNELS),
    [channels]
  );

  const displayChannelIdsKey = useMemo(
    () => displayChannels.map((c) => c.channelId).join(','),
    [displayChannels]
  );

  const [channelTabEdgeClones, setChannelTabEdgeClones] = useState(
    CHANNEL_TAB_EDGE_CLONES
  );

  const channelSwiperPad = useMemo(
    () => buildChannelSwiperPad(displayChannels, channelTabEdgeClones),
    [displayChannels, channelTabEdgeClones]
  );

  const initialChannelSlideIndex = useMemo(() => {
    const { cloneCount } = channelSwiperPad;
    if (!displayChannels.length) return 0;
    const sbsIx = displayChannels.findIndex((c) => c.channelId === 'S01');
    const logicalIx = sbsIx >= 0 ? sbsIx : 0;
    return cloneCount + logicalIx;
  }, [channelSwiperPad, displayChannels]);

  const channelTabCloneCountRef = useRef(0);
  channelTabCloneCountRef.current = channelSwiperPad.cloneCount;
  const [selectedChannelId, setSelectedChannelId] = useState('S01');
  const selectedChannelIdRef = useRef('S01');
  useEffect(() => {
    selectedChannelIdRef.current = selectedChannelId;
  }, [selectedChannelId]);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [scheduleByDate, setScheduleByDate] = useState(() =>
    organizeByDate(props.scheduleData)
  );
  const [alarms, setAlarms] = useState({});
  const [alarmsReady, setAlarmsReady] = useState(false);

  /** SSR 은 `{}` — 마운트 후 localStorage 복원 (새로고침·WebView 재진입) */
  useEffect(() => {
    setAlarms(readAlarmsFromStorage());
    setAlarmsReady(true);
  }, []);

  /** Android — intent 복귀 시 앱이 storage 만 갱신하고 JS 콜백을 안 줄 때 동기화 */
  useEffect(() => {
    if (!isAndroid) return undefined;
    const resync = () => setAlarms(readAlarmsFromStorage());
    const onVisible = () => {
      if (!document.hidden) resync();
    };
    window.addEventListener('pageshow', resync);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('pageshow', resync);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAndroid]);

  const channelSwiperRef = useRef(null);
  const dateTabsListRef = useRef(null);
  const channelPrevRef = useRef(null);
  const channelNextRef = useRef(null);
  const fetchingScheduleKeys = useRef(new Set());
  /** 날짜+채널 편성 fetch 가 한 번 끝난 키(빈 배열 응답 포함). 빈 placeholder 만 있을 때 재요청 루프 방지 */
  const scheduleFetchedKeysRef = useRef(new Set());
  const liveProgramRowRef = useRef(null);
  const firstProgramRowRef = useRef(null);
  /** 오늘 + 'live' 스크롤: LIVE 슬롯이 없어도 현재 시각에 가장 가까운 행 */
  const scheduleAnchorRowRef = useRef(null);
  /** 'live': 오늘·채널 변경 시 LIVE 행 | 'first': 오늘 외 날짜 선택 시 첫 편성 */
  const scheduleScrollTargetRef = useRef('live');
  /** 오늘+LIVE 정렬 완료 시그니처 — liveIndex·앵커·채널탭 레이아웃까지 포함해 재정렬 허용 */
  const scheduleLiveScrollSignatureRef = useRef('');
  /** 채널 Swiper init·align 완료 토큰 — SBS 디폴트 시 리마운트 전 스크롤 방지 */
  const [channelSwiperScrollToken, setChannelSwiperScrollToken] = useState(0);

  const [scheduleClock, setScheduleClock] = useState(() => Date.now());
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const id = window.setInterval(() => setScheduleClock(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);


  const todayStrRef = useRef(todayStr);
  todayStrRef.current = todayStr;

  /**
   * Android type-aos — document 스크롤 + sticky (페이지 이탈 시 인라인 스타일 복원).
   */
  useIsomorphicLayoutEffect(() => {
    if (!isAndroid) return undefined;
    ensureTypeAosScheduleDocumentScrollLayout();
    return () => {
      restoreTypeAosScheduleDocumentScrollLayout();
    };
  }, [isAndroid]);

  /** 태블릿·데스크톱 — 뷰포트 너비에 맞춰 양끝 클론 수 조정 (SBS 디폴트·가운데 정렬) */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const refreshEdgeClones = () => {
      const sw = channelSwiperRef.current;
      let slideWidth = CHANNEL_TAB_SLIDE_WIDTH_EST;
      if (sw && !sw.destroyed) {
        const firstSlide = sw.slides?.[sw.activeIndex] || sw.slides?.[0];
        const measured = firstSlide?.offsetWidth;
        if (measured > 0) slideWidth = measured;
      }
      const next = estimateChannelTabEdgeClones(window.innerWidth, slideWidth);
      setChannelTabEdgeClones((prev) => (prev === next ? prev : next));
    };

    refreshEdgeClones();
    window.addEventListener('resize', refreshEdgeClones);
    return () => window.removeEventListener('resize', refreshEdgeClones);
  }, [displayChannels.length, channelSwiperPad.cloneCount]);

  const allDatesKey = useMemo(
    () => allDates.map((d) => getApiDate(d)).join(','),
    [allDates]
  );

  useIsomorphicLayoutEffect(() => {
    const listEl = dateTabsListRef.current;
    const dateStr = selectedDate;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const run = () => {
      if (cancelled || attempts >= maxAttempts) return;
      attempts += 1;
      const ok = scrollDateTabIntoView(listEl, dateStr);
      if (!ok) requestAnimationFrame(run);
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [selectedDate, allDatesKey]);

  /** Swiper 기본 active 클래스와 React className 충돌 방지 — 선택 채널 1개만 활성 UI */
  const syncChannelTabActiveClass = useCallback(() => {
    const sw = channelSwiperRef.current;
    const chs = channelsRef.current.length ? channelsRef.current : displayChannels;
    if (!sw || sw.destroyed || !chs.length) return;
    const cloneCount = channelTabCloneCountRef.current;
    const curIx = chs.findIndex(
      (c) => String(c.channelId) === String(selectedChannelIdRef.current)
    );
    const logicalIx =
      curIx >= 0 ? curIx : channelTabLogicalIndex(sw.activeIndex, cloneCount, chs.length);
    const targetSlideIx = cloneCount + logicalIx;
    sw.slides?.forEach((slideEl, i) => {
      slideEl.classList.toggle('swiper-slide-active', i === targetSlideIx);
    });
  }, [displayChannels]);

  /** 선택 채널(selectedChannelId) 기준으로 Swiper 위치 맞춤 — activeIndex 역동기화 금지 */
  const alignChannelSwiperToSelection = useCallback(() => {
    const sw = channelSwiperRef.current;
    const chs = channelsRef.current.length ? channelsRef.current : displayChannels;
    if (!sw || sw.destroyed || !chs.length) return;
    const cloneCount = channelTabCloneCountRef.current;
    const curIx = chs.findIndex(
      (c) => String(c.channelId) === String(selectedChannelIdRef.current)
    );
    const logicalIx = curIx >= 0 ? curIx : 0;
    const targetSlide = cloneCount + logicalIx;
    try {
      sw.update();
      sw.slideTo(targetSlide, 0);
      syncChannelTabActiveClass();
    } catch {
      // ignore
    }
  }, [displayChannels, syncChannelTabActiveClass]);

  useIsomorphicLayoutEffect(() => {
    syncChannelTabActiveClass();
  }, [
    selectedChannelId,
    displayChannels,
    channelSwiperPad.cloneCount,
    syncChannelTabActiveClass,
  ]);

  /** 채널 목록 로드·패딩 슬라이드 변경 후 Swiper 너비 재계산 (WebView 에서 0폭·미노출 방지) */
  useEffect(() => {
    const sw = channelSwiperRef.current;
    if (!sw || sw.destroyed) return undefined;

    const runUpdate = () => {
      alignChannelSwiperToSelection();
    };

    runUpdate();
    const rafId = requestAnimationFrame(runUpdate);

    return () => cancelAnimationFrame(rafId);
  }, [
    displayChannels,
    channelSwiperPad.cloneCount,
    channelTabEdgeClones,
    alignChannelSwiperToSelection,
  ]);

  const onChannelSwiperInit = useCallback((swiper) => {
    channelSwiperRef.current = swiper;
    let removeNavClick = () => {};
    const bindNavArrows = () => {
      removeNavClick();
      const prevBtn = channelPrevRef.current;
      const nextBtn = channelNextRef.current;
      if (!prevBtn || !nextBtn) return;
      const syncFromLogicalIx = (logicalIx) => {
        const chs = channelsRef.current;
        if (!chs.length) return;
        const len = chs.length;
        const ix = ((logicalIx % len) + len) % len;
        const ch = chs[ix];
        if (!ch) return;
        const id = String(ch.channelId);
        scheduleScrollTargetRef.current = 'live';
        scheduleLiveScrollSignatureRef.current = '';
        selectedChannelIdRef.current = id;
        setSelectedChannelId(id);
        setSelectedDate(todayStrRef.current);
      };
      const step = (delta) => {
        const chs = channelsRef.current;
        const sw = channelSwiperRef.current;
        if (!sw || sw.destroyed || !chs.length) return;
        const len = chs.length;
        const cloneCount = channelTabCloneCountRef.current;
        const curIx = chs.findIndex(
          (c) => String(c.channelId) === String(selectedChannelIdRef.current)
        );
        const logicalIx =
          curIx >= 0
            ? curIx
            : channelTabLogicalIndex(sw.activeIndex, cloneCount, len);
        const nextLogical = (logicalIx + delta + len) % len;
        const nextSlide = nextLogical + cloneCount;
        syncFromLogicalIx(nextLogical);
        window.requestAnimationFrame(() => {
          const s2 = channelSwiperRef.current;
          if (!s2 || s2.destroyed) return;
          s2.slideTo(nextSlide, 0);
          syncChannelTabActiveClass();
        });
      };
      const onPrev = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        step(-1);
      };
      const onNext = (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        step(1);
      };
      prevBtn.addEventListener('click', onPrev, true);
      nextBtn.addEventListener('click', onNext, true);
      removeNavClick = () => {
        prevBtn.removeEventListener('click', onPrev, true);
        nextBtn.removeEventListener('click', onNext, true);
      };
    };
    const onDestroy = () => {
      removeNavClick();
    };
    swiper.on('destroy', onDestroy);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        bindNavArrows();
        alignChannelSwiperToSelection();
        /** Swiper 리마운트·align 이후 LIVE 정렬 (채널 탭 전환과 동일 타이밍) */
        scheduleLiveScrollSignatureRef.current = '';
        setChannelSwiperScrollToken((t) => t + 1);
      });
    });
  }, [alignChannelSwiperToSelection, syncChannelTabActiveClass]);

  const currentPrograms = useMemo(() => {
    const sid = String(selectedChannelId);
    const dayChannels = scheduleByDate[selectedDate] || [];
    const matches = dayChannels.filter((ch) => String(ch.channelId) === sid);
    if (!matches.length) return [];
    const best = matches.reduce((a, b) =>
      (b.programs?.length || 0) > (a.programs?.length || 0) ? b : a
    );
    return best.programs || [];
  }, [scheduleByDate, selectedDate, selectedChannelId]);

  /** 해당 날짜·채널 편성 API 응답 완료 여부 (로딩 중에는 빈 배열과 구분) */
  const isCurrentScheduleLoaded = useMemo(() => {
    const sid = String(selectedChannelId);
    const dayChannels = scheduleByDate[selectedDate];
    if (!dayChannels) return false;
    return dayChannels.some((ch) => String(ch.channelId) === sid);
  }, [scheduleByDate, selectedDate, selectedChannelId]);

  const selectedChannelName = useMemo(() => {
    const found = displayChannels.find(
      (c) => String(c.channelId) === String(selectedChannelId)
    );
    if (found?.channelName) return found.channelName;
    return String(selectedChannelId) === 'S01' ? 'SBS' : '';
  }, [displayChannels, selectedChannelId]);

  const liveIndex = useMemo(() => {
    if (selectedDate !== todayStr) return -1;
    return findLiveProgramIndex(currentPrograms, scheduleClock);
  }, [currentPrograms, scheduleClock, selectedDate, todayStr]);

  /** 채널 이동 후에도 오늘이면 현재 시각 기준 행으로 스크롤 (liveIndex 없을 때 공백 구간 보정) */
  const scheduleScrollAnchorIndex = useMemo(() => {
    if (selectedDate !== todayStr || !currentPrograms.length) return -1;
    const ix = findLiveProgramIndex(currentPrograms, scheduleClock);
    if (ix >= 0) return ix;
    return findTimeAnchorProgramIndex(currentPrograms, scheduleClock);
  }, [selectedDate, todayStr, currentPrograms, scheduleClock]);

  const adAfterIndex = useMemo(() => {
    const len = currentPrograms.length;
    if (!len) return -1;
    if (liveIndex < 0) {
      return Math.min(2, len - 1);
    }
    return Math.min(liveIndex + 2, len - 1);
  }, [liveIndex, currentPrograms.length]);

  /** LIVE/첫 행 스크롤 정렬 — 페인트 전 즉시 이동(스크롤 모션 없음) */
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined' || !currentPrograms.length) {
      return undefined;
    }

    const wantLiveAlign =
      scheduleScrollTargetRef.current === 'live' &&
      selectedDate === todayStr &&
      scheduleScrollAnchorIndex >= 0;

    /** 과거 날짜 탭 선택 시에만 첫 편성으로 — wantLiveAlign false 일 때 기본 fallback 금지 */
    const wantFirstAlign =
      scheduleScrollTargetRef.current === 'first' && selectedDate !== todayStr;

    if (!wantLiveAlign && !wantFirstAlign) {
      return undefined;
    }

    if (wantLiveAlign && (channels.length === 0 || channelSwiperScrollToken === 0)) {
      return undefined;
    }

    const scrollSignature = wantLiveAlign
      ? [
          selectedChannelId,
          selectedDate,
          channelTabEdgeClones,
          displayChannelIdsKey,
          liveIndex,
          scheduleScrollAnchorIndex,
          channelSwiperScrollToken,
        ].join('|')
      : '';

    if (wantLiveAlign && scheduleLiveScrollSignatureRef.current === scrollSignature) {
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    let didAlignSwiper = false;
    const maxAttempts = 12;

    const doScroll = () => {
      if (cancelled || attempts >= maxAttempts) return;
      attempts += 1;

      if (wantLiveAlign && !didAlignSwiper && channelSwiperRef.current) {
        didAlignSwiper = true;
        alignChannelSwiperToSelection();
      }

      const rowEl = wantLiveAlign
        ? liveIndex >= 0
          ? liveProgramRowRef.current || scheduleAnchorRowRef.current
          : scheduleAnchorRowRef.current
        : firstProgramRowRef.current;
      if (!rowEl) {
        requestAnimationFrame(doScroll);
        return;
      }
      scrollScheduleRowBelowTimetableTabs(rowEl);
      if (wantLiveAlign) {
        scheduleLiveScrollSignatureRef.current = scrollSignature;
      }
    };

    /** SBS 디폴트: 채널 Swiper·클론 수 확정 후 2프레임 뒤 LIVE 정렬 */
    const run = () => {
      if (wantLiveAlign) {
        requestAnimationFrame(() => {
          requestAnimationFrame(doScroll);
        });
      } else {
        doScroll();
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    selectedDate,
    todayStr,
    liveIndex,
    selectedChannelId,
    currentPrograms,
    scheduleScrollAnchorIndex,
    channelTabEdgeClones,
    displayChannelIdsKey,
    channels.length,
    channelSwiperScrollToken,
    alignChannelSwiperToSelection,
  ]);

  useEffect(() => {
    if (!channels.length) return;
    if (channels.some((c) => String(c.channelId) === String(selectedChannelId))) return;
    const fallback =
      channels.find((c) => String(c.channelId) === 'S01')?.channelId ?? channels[0].channelId;
    setSelectedChannelId(String(fallback));
  }, [channels, selectedChannelId]);

  const ensureChannelScheduleLoaded = useCallback(
    async (dateStrRaw, channelIdRaw) => {
      const dateStr = toYmdKey(dateStrRaw) || String(dateStrRaw || '');
      const channelId = String(channelIdRaw);
      if (!dateStr || dateStr.length !== 8 || !channelId) return;

      const row = displayChannels.find((c) => String(c.channelId) === channelId);
      if (!row) return;

      const fetchKey = `${dateStr}__${channelId}`;
      if (fetchingScheduleKeys.current.has(fetchKey)) return;

      const daySlice = scheduleByDate[dateStr];
      const existingRow = daySlice?.find((ch) => String(ch.channelId) === channelId);
      if (existingRow?.programs?.length > 0) return;
      if (scheduleFetchedKeysRef.current.has(fetchKey)) return;

      fetchingScheduleKeys.current.add(fetchKey);
      let fetchFinishedOk = false;

      try {
        const y = parseInt(dateStr.slice(0, 4), 10);
        const m = parseInt(dateStr.slice(4, 6), 10);
        const d = parseInt(dateStr.slice(6, 8), 10);
        const data = await fetchScheduleForDate(y, m, d, row);

        setScheduleByDate((prev) => {
          const next = { ...prev };
          const organized = organizeByDate(data);
          const mergeDay = (dk, incomingList) => {
            if (!dk) return;
            const incoming = Array.isArray(incomingList) ? incomingList : [];
            const existing = next[dk] !== undefined ? [...next[dk]] : [];
            incoming.forEach((ch) => {
              const sid = String(ch.channelId);
              const ix = existing.findIndex((x) => String(x.channelId) === sid);
              if (ix >= 0) existing[ix] = ch;
              else existing.push(ch);
            });
            next[dk] = existing;
          };

          Object.entries(organized).forEach(([date, chs]) => {
            mergeDay(toYmdKey(date), chs);
          });

          const sk = toYmdKey(dateStr);
          const normKeys = Object.keys(organized)
            .map((k) => toYmdKey(k))
            .filter(Boolean);
          const gotRequestedDay = sk && normKeys.includes(sk);
          let remapped = false;
          if (sk && normKeys.length === 1 && normKeys[0] !== sk) {
            const rawKey = Object.keys(organized).find((k) => toYmdKey(k) === normKeys[0]);
            const sole = rawKey != null ? organized[rawKey] : [];
            if (Array.isArray(sole) && sole.length) {
              mergeDay(sk, sole);
              delete next[normKeys[0]];
              remapped = true;
            }
          }
          if (sk && !gotRequestedDay && !remapped) {
            const existing = next[sk] !== undefined ? [...next[sk]] : [];
            const ix = existing.findIndex((x) => String(x.channelId) === channelId);
            const emptyCh = {
              date: sk,
              channelId,
              channelName: row.channelName,
              programs: [],
            };
            if (ix >= 0) existing[ix] = emptyCh;
            else existing.push(emptyCh);
            next[sk] = existing;
          }
          return next;
        });
        fetchFinishedOk = true;
      } catch (e) {
        console.error('스케줄 로드 실패:', e);
        setScheduleByDate((prev) => {
          const k = toYmdKey(dateStr);
          if (!k) return prev;
          const existing = prev[k] !== undefined ? [...prev[k]] : [];
          if (existing.some((ch) => String(ch.channelId) === channelId)) return prev;
          return {
            ...prev,
            [k]: [
              ...existing,
              {
                date: k,
                channelId,
                channelName: row.channelName,
                programs: [],
              },
            ],
          };
        });
      } finally {
        fetchingScheduleKeys.current.delete(fetchKey);
        if (fetchFinishedOk) scheduleFetchedKeysRef.current.add(fetchKey);
      }
    },
    [scheduleByDate, displayChannels]
  );

  useEffect(() => {
    if (!selectedDate || selectedDate.length !== 8) return;
    ensureChannelScheduleLoaded(selectedDate, selectedChannelId);
  }, [selectedDate, selectedChannelId, ensureChannelScheduleLoaded]);

  const handleChannelClick = useCallback(
    (channelId) => {
      const cid = String(channelId);
      const chs = channelsRef.current;
      const ix = chs.findIndex((c) => String(c.channelId) === cid);
      if (ix < 0) return;
      const slideIndex = channelTabCloneCountRef.current + ix;
      scheduleScrollTargetRef.current = 'live';
      scheduleLiveScrollSignatureRef.current = '';
      selectedChannelIdRef.current = cid;
      setSelectedChannelId(cid);
      const sw = channelSwiperRef.current;
      if (sw && chs.length) {
        sw.slideTo(slideIndex, 0);
        syncChannelTabActiveClass();
      }

      setSelectedDate(todayStr);
    },
    [todayStr, syncChannelTabActiveClass]
  );

  const handleDateClick = useCallback(
    (dateStr) => {
      scheduleScrollTargetRef.current = dateStr === todayStr ? 'live' : 'first';
      scheduleLiveScrollSignatureRef.current = '';
      setSelectedDate(dateStr);
    },
    [todayStr]
  );

  const handleAlarmToggle = useCallback((channelId, date, isActive) => {
    setAlarms((prev) => {
      const key = `${channelId}_${date}`;
      const next = { ...prev };
      if (isActive) next[key] = true;
      else delete next[key];
      return next;
    });
  }, []);

  /** iOS·Android·엔드 페이지와 동일 — toggleOnairAlarmActive + localStorage 동기화 */
  useEffect(() => {
    if (!alarmsReady || typeof window === 'undefined') return undefined;
    try {
      localStorage.setItem('alarms', JSON.stringify(alarms));
    } catch {
      // ignore
    }
    window.toggleOnairAlarmActive = (isActive, channelId, date) => {
      const cId = decodeURIComponent(channelId);
      const d = decodeURIComponent(date);
      handleAlarmToggle(cId, d, normalizeAlarmIsActive(isActive));
    };
    return () => {
      delete window.toggleOnairAlarmActive;
    };
  }, [alarms, alarmsReady, handleAlarmToggle]);

  return (
    <>
      <Head>
        <script
          dangerouslySetInnerHTML={{
            __html: `
        (function() {
          function applyDarkMode() {
            const userAgent = navigator.userAgent;
            const isSbsApp = userAgent.indexOf('SBSApp') > -1;
            const isPassPath = window.location.pathname.includes('/m/live/app');
            if (!(isSbsApp && isPassPath)) return;

            const themeMatch = userAgent.match(/theme\\/(light|dark)/i);
            if (themeMatch && themeMatch[1].toLowerCase() === 'light') return;

            // 외부 CSS 도착 전 흰 깜빡임 방지: 루트 배경을 즉시 다크(#1A1A1A)로 칠함
            document.documentElement.style.backgroundColor = '#1A1A1A';

            function paintBody() {
              if (!document.body) return false;
              document.body.style.backgroundColor = '#1A1A1A';
              document.body.classList.add('theme-dark', 'theme-dark2');
              return true;
            }
            if (!paintBody()) {
              const observer = new MutationObserver(function() {
                if (paintBody()) observer.disconnect();
              });
              observer.observe(document.documentElement, { childList: true });
            }
          }
          applyDarkMode();
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', applyDarkMode);
          }
        })();
      `,
          }}
        />
        <link
          rel="stylesheet"
          href={`${process.env.NEXT_PUBLIC_GLOBAL_CSS_M_URL}?v=${process.env.NEXT_PUBLIC_BUILD_TIME}`}
        />
        <meta
          name="viewport"
          content="user-scalable=no, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, width=device-width"
        />
        <script
          type="text/javascript"
          src={process.env.NEXT_PUBLIC_GNB_JS_URL}
        />
        <title>편성표 | SBS</title>
      </Head>

      <div role="main" id="container" className="contents">
        <div className={`timetable_wrap app2026${isAndroid ? ' type-aos' : ''}`}>
          <div className="timetableTab_w">

            <div className="tab-type002">
              <Swiper
                key={`ch-${displayChannels.map((c) => c.channelId).join('-')}-e${channelTabEdgeClones}`}
                className="swiper-area"
                wrapperTag="ul"
                onSwiper={onChannelSwiperInit}
                slidesPerView="auto"
                spaceBetween={8}
                centeredSlides
                centeredSlidesBounds
                roundLengths
                loop={false}
                slideActiveClass="swiper-slide-channel-tab-inert"
                initialSlide={initialChannelSlideIndex}
                allowTouchMove={false}
                observer
                observeParents
              >
                {channelSwiperPad.slides.map((ch, slideIdx) => (
                  <SwiperSlide key={`chpad-${slideIdx}-${ch.channelId}`} tag="li">
                    <a
                      href="#none"
                      className="tab"
                      onClick={(e) => {
                        e.preventDefault();
                        handleChannelClick(ch.channelId);
                      }}
                    >
                      <span className="txt01">{ch.channelName}</span>
                    </a>
                  </SwiperSlide>
                ))}
              </Swiper>
              <button
                type="button"
                ref={channelPrevRef}
                className="btn-dir001-prev"
              >
                <i className="ir">이전 리스트</i>
              </button>
              <button
                type="button"
                ref={channelNextRef}
                className="btn-dir001-next"
              >
                <i className="ir">다음 리스트</i>
              </button>
            </div>

            <div className="tab-type001">
              <ul className="list01" ref={dateTabsListRef}>
                {allDates.map((date) => {
                  const dateStr = getApiDate(date);
                  const isToday = dateStr === todayStr;
                  const isActive = dateStr === selectedDate;
                  return (
                    <li
                      key={dateStr}
                      data-date={dateStr}
                      className={[isActive ? 'active' : '', isToday ? 'today' : '']
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <a
                        href="#none"
                        className="tab"
                        onClick={(e) => {
                          e.preventDefault();
                          handleDateClick(dateStr);
                        }}
                      >
                        <span className="txt01">
                          {isToday ? '오늘' : DAY_LABELS[date.getDay()]}
                        </span>
                        <span className="txt02">
                          {String(date.getDate()).padStart(2, '0')}
                        </span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {currentPrograms.length > 0 ? (
            <div className="schedule_table_w active">
              {currentPrograms.map((program, idx) => {
                const alarmDate = formatAlarmDateKst(program.startTimestamp);
                return (
                  <ScheduleProgramItem
                    key={`${program.startTimestamp}_${idx}`}
                    program={program}
                    channelId={selectedChannelId}
                    channelName={selectedChannelName}
                    jwtToken={props.jwtToken}
                    isAlarmActive={isAlarmActiveForDisplay(
                      alarms,
                      selectedChannelId,
                      alarmDate,
                      props.jwtToken
                    )}
                    onAlarmToggle={(isActive) =>
                      handleAlarmToggle(selectedChannelId, alarmDate, isActive)
                    }
                    showAd={selectedDate === todayStr && idx === adAfterIndex}
                    nowTs={scheduleClock}
                    scrollAnchorRef={(el) => {
                      if (idx === 0) firstProgramRowRef.current = el;
                      if (liveIndex >= 0 && idx === liveIndex) {
                        liveProgramRowRef.current = el;
                      }
                      if (idx === scheduleScrollAnchorIndex) {
                        scheduleAnchorRowRef.current = el;
                      }
                    }}
                    programIndex={idx}
                    liveRowIndex={liveIndex}
                    viewingToday={selectedDate === todayStr}
                    alarmDate={alarmDate}
                    isTypeAos={isAndroid}
                  />
                );
              })}

              <ScheduleInfoDott />
            </div>
          ) : isCurrentScheduleLoaded ? (
            <div className="schedule_no_info_w">
              <div className="txt-type001">
                <p className="txt01">편성 정보가 없습니다.</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

function buildLiveAppSchedulePath(query) {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value == null) return;
    const values = Array.isArray(value) ? value : [value];
    values.forEach((v) => params.append(key, String(v)));
  });
  const search = params.toString();
  return `/m/live/app/schedule${search ? `?${search}` : ''}`;
}

/** deviceDetector가 SBS_DL=en 으로 /en/ 리다이렉트하기 전에 ko 로 맞춤 (앱 WebView 전용) */
function setKoLocaleCookieForSbsApp(ctx) {
  const userAgent = ctx.req.headers['user-agent'] || '';
  if (!userAgent.includes('SBSApp')) return;

  const expires = new Date();
  expires.setDate(expires.getDate() + 1);
  ctx.res.setHeader(
    'Set-Cookie',
    `SBS_DL=ko; Path=/; Expires=${expires.toUTCString()}`
  );
}

export async function getServerSideProps(ctx) {
  const { cookies } = ctx.req;
  const { LOGIN_JWT } = cookies;

  setKoLocaleCookieForSbsApp(ctx);

  if (ctx.locale && ctx.locale !== ctx.defaultLocale) {
    return {
      redirect: {
        destination: buildLiveAppSchedulePath(ctx.query),
        permanent: false,
      },
    };
  }

  /**
   * 첫 화면 cold 빈화면 제거: 빠른 정적 CDN(SBS.json)인 오늘(KST) S01 편성만 서버에서 받아
   * HTML 에 미리 그려 내려보낸다. 느린 채널 목록 API 는 TTFB 를 늘리지 않도록 클라에서 로드.
   */
  const seoul = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
  );
  const y = seoul.getFullYear();
  const m = seoul.getMonth() + 1;
  const d = seoul.getDate();

  let scheduleData = [];
  try {
    scheduleData = await fetchScheduleForDate(y, m, d, {
      channelId: 'S01',
      channelName: 'SBS',
    });
  } catch (e) {
    console.error('편성표 SSR 초기 편성 로드 실패:', e?.message || e);
    scheduleData = [];
  }

  const initialDateYmd = `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;

  return {
    props: {
      scheduleData: scheduleData || [],
      channels: [],
      jwtToken: LOGIN_JWT || null,
      initialDateYmd,
    },
  };
}

export default SchedulePage;
